import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  toast,
} from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeleteRoadmapNode } from '@/api/rest/resources/organization';
import { RoadmapNode } from '@/api/rest/resources/types/organization';
import { isNullish } from '@/utils/booleans';

export type DeleteRoadmapNodeProps = {
  roadmapNode: RoadmapNode | null;
  onClose: () => void;
  onSuccess?: () => void;
};

export const DeleteRoadmapNodeWithConfirmation = ({ roadmapNode, onClose, onSuccess }: DeleteRoadmapNodeProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [deletingRoadmapNode, setDeletingRoadmapNode] = useState(false);

  const deleteRoadmapNodeMutation = useDeleteRoadmapNode();

  const deleteRoadmapNode = async () => {
    if (!roadmapNode) return;

    if (isNullish(roadmapNode.id) || isNullish(roadmapNode.organization)) {
      toast({
        type: 'error',
        title: t('shared.goals.deleteNode.toasts.error'),
      });
      return;
    }

    try {
      setDeletingRoadmapNode(true);
      await deleteRoadmapNodeMutation.mutateAsync({
        pathVariables: { organization_id: roadmapNode.organization, roadmap_node_id: roadmapNode.id },
      });

      toast({
        type: 'success',
        title: t('shared.goals.deleteNode.toasts.success'),
      });

      if (onSuccess) {
        onSuccess();
      }

      queryClient.invalidateQueries({
        queryKey: ['organization', { organization_id: roadmapNode.organization }, 'roadmap-nodes'],
      });

      onClose();
    } catch (error) {
      toast({
        type: 'error',
        title: t('shared.goals.deleteNode.toasts.error'),
      });
    } finally {
      setDeletingRoadmapNode(false);
    }
  };

  return (
    <Dialog open={!!roadmapNode} onOpenChange={onClose}>
      <DialogContent data-testid='delete-roadmapNode-with-confirmation'>
        <DialogHeader className='typography-h2 w-full text-error md:typography-h3 sm:text-center'>
          {t('shared.goals.deleteNode.dialog.title')}
        </DialogHeader>
        <DialogBody className='mb-8'>
          {t('shared.goals.deleteNode.dialog.message', { title: roadmapNode?.title })}
        </DialogBody>
        <DialogFooter className='justify-end'>
          <DialogClose asChild>
            <Button variant='text'>{t('global.ui.buttons.cancel')}</Button>
          </DialogClose>
          <Button
            onClick={() => deleteRoadmapNode()}
            loading={deletingRoadmapNode}
            className='bg-error hover:bg-error-dark'
          >
            {t('shared.goals.deleteNode.dialog.submitButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
